import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Statistic
const StatisticModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(
        initialData || { title: "", value: "", description: "" }
    );
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = "Title is required";
        if (!formData.value) newErrors.value = "Value is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            await onSubmit(formData);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Statistic" : "Add Statistic"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter title"
                        />
                        {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Value</label>
                        <input
                            type="text"
                            name="value"
                            value={formData.value}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter value"
                        />
                        {errors.value && <p className="text-red-400 text-sm mt-1">{errors.value}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Description (optional)</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter description"
                            rows="4"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function Statistics() {
    const [statistics, setStatistics] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editStatistic, setEditStatistic] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all statistics
    useEffect(() => {
        const fetchStatistics = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/statistics", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStatistics(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch statistics. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchStatistics();
    }, [token]);

    // Handle Add Statistic
    const handleAddStatistic = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/statistics",
                data,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStatistics([...statistics, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Statistic added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add statistic.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Statistic
    const handleEditStatistic = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.put(
                `https://ligasos-laravel.ligasos.co/api/statistics/${editStatistic.id}`,
                data,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStatistics(statistics.map((s) => (s.id === editStatistic.id ? response.data.data : s)));
            setIsEditModalOpen(false);
            setEditStatistic(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Statistic updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update statistic.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Statistic
    const handleDeleteStatistic = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/statistics/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStatistics(statistics.filter((s) => s.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Statistic deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete statistic.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Statistics</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Statistic
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : statistics.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No statistics found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Value</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Description</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {statistics.map((statistic, index) => (
                                    <tr
                                        key={statistic.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{statistic.title || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{statistic.value || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{statistic.description || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditStatistic(statistic);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteStatistic(statistic.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <StatisticModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddStatistic}
                isEdit={false}
            />
            <StatisticModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditStatistic(null);
                }}
                onSubmit={handleEditStatistic}
                initialData={editStatistic}
                isEdit={true}
            />
        </section>
    );
}